import { applyI18n, t } from '../i18n';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { createHelpTip } from '../help/helpTips';
import { bindModalDismiss, createModalCloseButton } from './modalDialogShell';
import {
  ODT_MIME,
  SPREADSHEET_MAX_COLS,
  SPREADSHEET_MAX_ROWS,
  SNIFF_HEADER_BYTES,
  extensionOf,
  innerNameAfterGzip,
  resolveFilePreview,
  type FilePreviewKind,
} from '../files/fileTypes';
import { gunzip } from '../files/gzip';
import { odtToHtml } from '../files/odtParse';
import { parseTar } from '../files/tarParse';
import { renderArchiveListing, resolveArchiveEntry, type ArchiveEntry } from './archivePreview';
import { renderCertificatePreview } from './certPreview';
import { renderEmailPreview } from './emlPreview';
import { renderHarPreview } from './harPreview';
import { renderPcapPreview } from './pcapPreview';
import { renderPptxPreview } from './pptxPreview';
import { renderPlainTextPreview, renderRichTextPreview } from './textPreview';
import { renderTiffPreview } from './tiffPreview';

export type FilePreviewGalleryItem = {
  id: string;
  fileName: string;
  contentType?: string | null;
};

export type FilePreviewGalleryOptions = {
  items: FilePreviewGalleryItem[];
  initialIndex: number;
  loadItem: (item: FilePreviewGalleryItem) => Promise<Blob>;
};

export async function showFilePreview(
  fileName: string,
  blob: Blob,
  contentType?: string | null,
  gallery?: FilePreviewGalleryOptions,
): Promise<void> {
  const header = new Uint8Array(await blob.slice(0, SNIFF_HEADER_BYTES).arrayBuffer());
  const resolved = resolveFilePreview(fileName, contentType ?? blob.type, header);
  const typed = resolved.mime && resolved.mime !== blob.type ? new Blob([blob], { type: resolved.mime }) : blob;

  if (resolved.kind === 'binary') {
    openDownloadOnlyDialog(fileName, typed);
    return;
  }

  await openPreviewDialog(fileName, typed, resolved.kind, resolved.mime, gallery);
}

function openDownloadOnlyDialog(fileName: string, blob: Blob): void {
  const { backdrop, dialog } = createDialogShell();
  dialog.classList.add('file-preview-dialog--unavailable');
  dialog.innerHTML = `
    <div class="file-preview-header">
      <h2 id="file-preview-title"></h2>
      <div class="file-preview-header-actions"></div>
    </div>
    <p class="muted file-preview-unavailable" id="file-preview-unavailable"></p>
    <div class="modal-dialog__actions">
      <button type="button" class="button primary" id="file-preview-download" data-i18n="sedr.evidence.download"></button>
    </div>
  `;

  const headerActions = dialog.querySelector('.file-preview-header-actions') as HTMLDivElement;
  headerActions.append(
    createHelpTip(dialogHelpTopic('filePreview')),
    createModalCloseButton({ id: 'file-preview-close', className: 'file-preview-close' }),
  );

  (dialog.querySelector('#file-preview-title') as HTMLElement).textContent = fileName;
  (dialog.querySelector('#file-preview-unavailable') as HTMLElement).textContent = t('sedr.evidence.binaryPreview');
  applyI18n(dialog);
  bindPreviewChrome(backdrop, dialog, fileName, blob);
}

async function openPreviewDialog(
  fileName: string,
  blob: Blob,
  kind: FilePreviewKind,
  mime: string,
  gallery?: FilePreviewGalleryOptions,
): Promise<void> {
  const { backdrop, dialog } = createDialogShell();
  const galleryActive = Boolean(gallery && gallery.items.length > 1);
  dialog.innerHTML = `
    <div class="file-preview-header">
      <h2 id="file-preview-title"></h2>
      <div class="file-preview-header-actions">
        ${galleryActive ? `
        <div class="file-preview-gallery-nav" id="file-preview-gallery-nav">
          <button type="button" class="button ghost file-preview-gallery-nav-btn" id="file-preview-gallery-prev" data-i18n-aria-label="files.preview.gallery.prev">&lt;</button>
          <span class="file-preview-gallery-status" id="file-preview-gallery-status"></span>
          <button type="button" class="button ghost file-preview-gallery-nav-btn" id="file-preview-gallery-next" data-i18n-aria-label="files.preview.gallery.next">&gt;</button>
        </div>
        ` : ''}
        <button type="button" class="button subtle" id="file-preview-download" data-i18n="sedr.evidence.download"></button>
      </div>
    </div>
    <div class="file-preview-viewport" id="file-preview-viewport">
      <div class="file-preview-content" id="file-preview-content"></div>
    </div>
  `;
  const headerActions = dialog.querySelector('.file-preview-header-actions') as HTMLDivElement;
  headerActions.prepend(createHelpTip(dialogHelpTopic('filePreview')));
  headerActions.append(createModalCloseButton({ id: 'file-preview-close', className: 'file-preview-close' }));
  (dialog.querySelector('#file-preview-title') as HTMLElement).textContent = fileName;
  applyI18n(dialog);

  const content = dialog.querySelector('#file-preview-content') as HTMLElement;
  const rendered = await renderPreview(content, fileName, kind, blob, mime);
  if (rendered === false) {
    backdrop.remove();
    openDownloadOnlyDialog(fileName, blob);
    return;
  }

  if (!galleryActive || !gallery) {
    bindPreviewChrome(backdrop, dialog, fileName, blob, rendered.objectUrl ?? null, rendered.dispose);
    return;
  }

  await bindGalleryPreviewChrome(backdrop, dialog, {
    items: gallery.items,
    index: Math.min(Math.max(0, gallery.initialIndex), gallery.items.length - 1),
    loadItem: gallery.loadItem,
    fileName,
    blob,
    objectUrl: rendered.objectUrl ?? null,
    dispose: rendered.dispose ?? null,
  });
}

function createDialogShell(): { backdrop: HTMLDivElement; dialog: HTMLDivElement } {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.setAttribute('role', 'presentation');
  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog file-preview-dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  backdrop.append(dialog);
  document.body.append(backdrop);
  return { backdrop, dialog };
}

function bindPreviewChrome(
  backdrop: HTMLDivElement,
  dialog: HTMLElement,
  fileName: string,
  blob: Blob,
  objectUrl: string | null = null,
  dispose: (() => void) | null = null,
): void {
  const close = () => {
    unbindDismiss();
    dispose?.();
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop,
    closeButton: dialog.querySelector('#file-preview-close') as HTMLButtonElement | null,
    onClose: close,
  });
  dialog.querySelector('#file-preview-download')?.addEventListener('click', () => {
    downloadBlob(fileName, blob);
  });
}

function isEditableKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

async function bindGalleryPreviewChrome(
  backdrop: HTMLDivElement,
  dialog: HTMLElement,
  state: {
    items: FilePreviewGalleryItem[];
    index: number;
    loadItem: (item: FilePreviewGalleryItem) => Promise<Blob>;
    fileName: string;
    blob: Blob;
    objectUrl: string | null;
    dispose: (() => void) | null;
  },
): Promise<void> {
  let closed = false;
  let loading = false;
  let currentFileName = state.fileName;
  let currentBlob = state.blob;
  let objectUrl = state.objectUrl;
  let dispose = state.dispose;
  let index = state.index;

  const titleEl = dialog.querySelector('#file-preview-title') as HTMLElement;
  const content = dialog.querySelector('#file-preview-content') as HTMLElement;
  const prevBtn = dialog.querySelector('#file-preview-gallery-prev') as HTMLButtonElement;
  const nextBtn = dialog.querySelector('#file-preview-gallery-next') as HTMLButtonElement;
  const statusEl = dialog.querySelector('#file-preview-gallery-status') as HTMLElement;

  const syncNav = () => {
    const current = index + 1;
    const total = state.items.length;
    statusEl.dataset.galleryCurrent = String(current);
    statusEl.dataset.galleryTotal = String(total);
    statusEl.textContent = t('files.preview.gallery.status', { current, total });
    prevBtn.disabled = loading || index <= 0;
    nextBtn.disabled = loading || index >= state.items.length - 1;
  };

  const cleanupCurrent = () => {
    dispose?.();
    dispose = null;
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  };

  const close = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeyDown);
    unbindDismiss();
    cleanupCurrent();
    backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop,
    closeButton: dialog.querySelector('#file-preview-close') as HTMLButtonElement | null,
    onClose: close,
  });

  dialog.querySelector('#file-preview-download')?.addEventListener('click', () => {
    downloadBlob(currentFileName, currentBlob);
  });

  const showItem = async (nextIndex: number) => {
    if (closed || loading || nextIndex === index) return;
    if (nextIndex < 0 || nextIndex >= state.items.length) return;

    const item = state.items[nextIndex]!;
    loading = true;
    syncNav();
    cleanupCurrent();
    content.replaceChildren();
    const loadingEl = document.createElement('p');
    loadingEl.className = 'muted file-preview-gallery-loading';
    loadingEl.textContent = t('files.preview.gallery.loading');
    content.append(loadingEl);

    try {
      const blob = await state.loadItem(item);
      if (closed) return;
      const header = new Uint8Array(await blob.slice(0, SNIFF_HEADER_BYTES).arrayBuffer());
      const resolved = resolveFilePreview(item.fileName, item.contentType ?? blob.type, header);
      const typed = resolved.mime && resolved.mime !== blob.type
        ? new Blob([blob], { type: resolved.mime })
        : blob;

      content.replaceChildren();
      if (resolved.kind === 'binary') {
        const unavailable = document.createElement('p');
        unavailable.className = 'muted file-preview-unavailable';
        unavailable.textContent = t('sedr.evidence.binaryPreview');
        content.append(unavailable);
        currentFileName = item.fileName;
        currentBlob = typed;
        titleEl.textContent = item.fileName;
        index = nextIndex;
        return;
      }

      const rendered = await renderPreview(content, item.fileName, resolved.kind, typed, resolved.mime);
      if (closed) {
        if (rendered !== false) {
          rendered.dispose?.();
          if (rendered.objectUrl) URL.revokeObjectURL(rendered.objectUrl);
        }
        return;
      }
      if (rendered === false) {
        const unavailable = document.createElement('p');
        unavailable.className = 'muted file-preview-unavailable';
        unavailable.textContent = t('sedr.evidence.binaryPreview');
        content.replaceChildren(unavailable);
        currentFileName = item.fileName;
        currentBlob = typed;
        titleEl.textContent = item.fileName;
        index = nextIndex;
        return;
      }

      currentFileName = item.fileName;
      currentBlob = typed;
      objectUrl = rendered.objectUrl ?? null;
      dispose = rendered.dispose ?? null;
      titleEl.textContent = item.fileName;
      index = nextIndex;
    } catch {
      if (closed) return;
      content.replaceChildren();
      const errorEl = document.createElement('p');
      errorEl.className = 'muted file-preview-gallery-error';
      errorEl.textContent = t('files.preview.gallery.error');
      content.append(errorEl);
    } finally {
      loading = false;
      if (!closed) syncNav();
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (closed || isEditableKeyTarget(event.target)) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      void showItem(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      void showItem(index + 1);
    }
  };

  prevBtn.addEventListener('click', () => {
    void showItem(index - 1);
  });
  nextBtn.addEventListener('click', () => {
    void showItem(index + 1);
  });
  document.addEventListener('keydown', onKeyDown);
  syncNav();
}

async function renderPreview(
  content: HTMLElement,
  fileName: string,
  kind: FilePreviewKind,
  blob: Blob,
  mime: string,
): Promise<{ objectUrl?: string | null; dispose?: () => void } | false> {
  switch (kind) {
    case 'image':
      return renderImagePreview(content, blob, mime);
    case 'pdf':
      return { objectUrl: attachMedia(content, 'iframe', 'file-preview-pdf', blob) };
    case 'video':
      return { objectUrl: attachMedia(content, 'video', 'file-preview-video', blob, { controls: true }) };
    case 'audio':
      return { objectUrl: attachMedia(content, 'audio', 'file-preview-audio', blob, { controls: true }) };
    case 'text':
      return renderTextKindPreview(content, blob, mime);
    case 'spreadsheet':
      return (await renderSpreadsheetPreview(content, blob, mime)) ? {} : false;
    case 'document':
      return (await renderDocumentPreview(content, blob, mime)) ? {} : false;
    case 'presentation':
      return await renderPptxPreview(content, blob);
    case 'archive':
      return await renderArchivePreview(content, fileName, blob, mime);
    case 'pcap':
      return (await renderPcapPreview(content, blob)) ? {} : false;
    case 'email':
      return (await renderEmailPreview(content, blob)) ? {} : false;
    case 'har':
      return (await renderHarPreview(content, blob)) ? {} : false;
    case 'certificate':
      return (await renderCertificatePreview(content, blob, fileName)) ? {} : false;
    default:
      return false;
  }
}

function attachMedia(
  content: HTMLElement,
  tag: 'img' | 'iframe' | 'video' | 'audio',
  className: string,
  blob: Blob,
  attrs?: { controls?: boolean },
): string {
  const el = document.createElement(tag);
  el.className = className;
  if (attrs?.controls && (tag === 'video' || tag === 'audio')) {
    (el as HTMLVideoElement | HTMLAudioElement).controls = true;
  }
  const url = URL.createObjectURL(blob);
  if (tag === 'iframe') {
    (el as HTMLIFrameElement).sandbox = '';
  }
  (el as HTMLImageElement | HTMLIFrameElement | HTMLVideoElement | HTMLAudioElement).src = url;
  content.append(el);
  return url;
}

async function renderImagePreview(
  content: HTMLElement,
  blob: Blob,
  mime: string,
): Promise<{ objectUrl?: string | null; dispose?: () => void } | false> {
  if (mime === 'image/tiff') {
    return renderTiffPreview(content, blob);
  }
  return { objectUrl: attachMedia(content, 'img', 'file-preview-image', blob) };
}

async function renderTextKindPreview(
  content: HTMLElement,
  blob: Blob,
  mime: string,
): Promise<{}> {
  if (mime === 'text/markdown' || mime === 'text/html' || mime === 'application/xhtml+xml') {
    await renderRichTextPreview(content, blob, mime);
    return {};
  }
  await renderPlainTextPreview(content, blob, mime);
  return {};
}

async function renderSpreadsheetPreview(content: HTMLElement, blob: Blob, mime: string): Promise<boolean> {
  try {
    const XLSX = await import('xlsx');
    const isCsv = mime === 'text/csv' || mime === 'application/csv';
    const isTsv = mime === 'text/tab-separated-values';
    const workbook = isCsv || isTsv
      ? XLSX.read(await blob.text(), { type: 'string', FS: isTsv ? '\t' : ',' })
      : XLSX.read(new Uint8Array(await blob.arrayBuffer()), { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return false;
    const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
      header: 1,
      defval: '',
      raw: false,
    }) as unknown[][];

    const wrap = document.createElement('div');
    wrap.className = 'file-preview-table-wrap';
    const table = document.createElement('table');
    table.className = 'data-table file-preview-table';

    const limited = rows.slice(0, SPREADSHEET_MAX_ROWS);
    for (const row of limited) {
      const tr = document.createElement('tr');
      const cells = row.slice(0, SPREADSHEET_MAX_COLS);
      for (const cell of cells) {
        const td = document.createElement('td');
        td.textContent = cell == null ? '' : String(cell);
        tr.append(td);
      }
      table.append(tr);
    }

    wrap.append(table);
    content.append(wrap);
    return true;
  } catch {
    return false;
  }
}

async function renderDocumentPreview(content: HTMLElement, blob: Blob, mime: string): Promise<boolean> {
  if (mime === ODT_MIME) {
    return renderOdtPreview(content, blob);
  }
  return renderDocxPreview(content, blob);
}

async function renderDocxPreview(content: HTMLElement, blob: Blob): Promise<boolean> {
  try {
    const mammoth = await import('mammoth');
    const arrayBuffer = await blob.arrayBuffer();
    const { value } = await mammoth.convertToHtml({ arrayBuffer });

    const iframe = document.createElement('iframe');
    iframe.className = 'file-preview-document';
    iframe.sandbox = '';
    iframe.referrerPolicy = 'no-referrer';
    iframe.srcdoc = value;
    content.append(iframe);
    return true;
  } catch {
    return false;
  }
}

async function renderOdtPreview(content: HTMLElement, blob: Blob): Promise<boolean> {
  try {
    const html = await odtToHtml(new Uint8Array(await blob.arrayBuffer()));
    const iframe = document.createElement('iframe');
    iframe.className = 'file-preview-document';
    iframe.sandbox = '';
    iframe.referrerPolicy = 'no-referrer';
    iframe.srcdoc = html;
    content.append(iframe);
    return true;
  } catch {
    return false;
  }
}

async function renderArchivePreview(
  content: HTMLElement,
  fileName: string,
  blob: Blob,
  mime: string,
): Promise<{} | false> {
  const bytes = new Uint8Array(await blob.arrayBuffer());

  if (mime === 'application/gzip' || mime === 'application/x-gzip') {
    return renderGzipPreview(content, fileName, bytes);
  }
  if (mime === 'application/x-tar' || mime === 'application/tar') {
    return renderTarListing(content, bytes);
  }
  return renderZipListing(content, bytes);
}

async function renderGzipPreview(
  content: HTMLElement,
  fileName: string,
  bytes: Uint8Array,
): Promise<{} | false> {
  try {
    const inflated = await gunzip(bytes);
    const innerName = innerNameAfterGzip(fileName);
    const ext = extensionOf(innerName);
    const isTar = ext === 'tar' || ext === 'tar.gz' || ext === 'tgz'
      || (inflated.length >= 265 && String.fromCharCode(...inflated.subarray(257, 262)) === 'ustar');

    if (isTar) {
      return renderTarListing(content, inflated);
    }

    const header = inflated.subarray(0, Math.min(inflated.length, SNIFF_HEADER_BYTES));
    const resolved = resolveFilePreview(innerName, null, header);
    if (resolved.kind === 'binary') return false;

    const innerBlob = new Blob([toArrayBuffer(inflated)], { type: resolved.mime });
    return renderPreview(content, innerName, resolved.kind, innerBlob, resolved.mime);
  } catch {
    return false;
  }
}

function renderTarListing(content: HTMLElement, bytes: Uint8Array): {} | false {
  const entries = parseTar(bytes);
  if (entries.length === 0) return false;
  const archiveEntries: ArchiveEntry[] = entries.map((entry) => ({
    name: entry.name,
    size: entry.size,
    resolved: resolveArchiveEntry(entry.name, entry.bytes),
    getBytes: async () => entry.bytes,
  }));
  renderArchiveListing(content, archiveEntries);
  return {};
}

async function renderZipListing(content: HTMLElement, bytes: Uint8Array): Promise<{} | false> {
  try {
    const jszipMod = await import('jszip');
    const zip = await jszipMod.default.loadAsync(bytes);
    const entries = Object.values(zip.files)
      .filter((f) => !f.dir)
      .map((f) => ({
        name: f.name,
        size: resolveZipEntrySize(f),
        zipEntry: f,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    if (entries.length === 0) return false;

    const archiveEntries: ArchiveEntry[] = [];
    for (const entry of entries) {
      const bytes = await entry.zipEntry.async('uint8array');
      archiveEntries.push({
        name: entry.name,
        size: entry.size,
        resolved: resolveArchiveEntry(entry.name, bytes),
        getBytes: async () => bytes,
      });
    }

    renderArchiveListing(content, archiveEntries);
    return {};
  } catch {
    return false;
  }
}

function resolveZipEntrySize(entry: unknown): number | null {
  const anyEntry = entry as { size?: number; _data?: { uncompressedSize?: number; size?: number } };
  if (typeof anyEntry?.size === 'number') return anyEntry.size;
  if (anyEntry?._data && typeof anyEntry._data.uncompressedSize === 'number') {
    return anyEntry._data.uncompressedSize;
  }
  if (anyEntry?._data && typeof anyEntry._data.size === 'number') {
    return anyEntry._data.size;
  }
  return null;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function downloadBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.click();
  URL.revokeObjectURL(url);
}